import * as React from 'react';
import { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { getLoadDataError, LoadDataApiError } from '@mc/axios-api';
import { UseQueryResult } from 'react-query';
import { AppError, Spinner, SpinnerSize } from '@mc/ui-v4';

type Props = {
  queries: UseQueryResult<any, any>[];
  children: (props: {
    /**
     * Data jsou načítána. Pokud jsou již načtena, signalizuje to jejich refresh.
     */
    isFetching: boolean;
    /**
     * Pokud je true, tak již má nějaká načtená data, false může být např. pokud se data načítají a showContentWhenLoading je true.
     */
    hasData: boolean;
  }) => ReactNode;
  fullscreen?: boolean;
  // Small verze loadingu pro inline Použití
  inline?: boolean;
  /**
   * Zobrazovat obsah i když nejsou ještě načtena data - využít lze např. u VirtualListu, kde jej chceme renderovat i bez dat - opticky vidět, že tam je.
   */
  showContentWhenLoading?: boolean;
};

/**
 * Komponenta pro čekání na načtení dat, řeší prvotní načtení dat - spinner, zobrazení případných error.
 *
 * Příklad použití:
 * ```
 * <WaitForQueryData queries={[organizaceCastCiselnik, zakonCiselnik, noveOznameni]} fullscreen>
 *   {() => (
 *      <div>...obsah stránky, zde jsoujiž načtená všechna data...</div>
 *   )}
 * </WaitForQueryData>
 * ```
 *
 * @param queries pole queries na které se čeká
 * @param fullscreen má se loading zobrazit na celou stránku?
 * @param children funkce, která má renderovat obsah stránky - je volána až poté, co sjou úspěšně načteny všechny query
 * @constructor
 */
export const WaitForQueryData: React.FC<Props> = ({
  inline,
  queries,
  fullscreen,
  showContentWhenLoading = false,
  children,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  let isError = false;
  let isLoading = false;
  let isSuccess = false;
  let isFetching = false;

  // let errorIntent: Intent = Intent.WARNING;
  let errors: LoadDataApiError[] = [];
  for (let a = 0; a < queries.length; a++) {
    const q = queries[a];

    isFetching = isFetching || q.isFetching;
    isSuccess = isSuccess || q.isSuccess;

    if (q.isError) {
      isError = true;

      const error = getLoadDataError(q.error);
      errors.push(error);
      // if (error.intent && error.intent === Intent.DANGER) {
      //     errorIntent = error.intent;
      // }
    }
    if (q.isLoading) {
      isLoading = true;
    }
  }
  let hasData = !isLoading || (isLoading && showContentWhenLoading);

  const spinnerCls = classNames('wait-for-query-wait', {
    ['wait-for-query-wait-inline']: inline,
    ['d-flex align-items-center h-100']: fullscreen,
  });

  if (isError) {
    return (
      <AppError message={'Data se nepodařilo načíst'}>
        <div className={'d-flex align-items-center flex-column'}>
          <a
            href={'#'}
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? 'Skrýt podrobnosti' : 'Zobrazit podrobnosti'}
          </a>
          {open && (
            <div className={'mt-2'}>
              {errors.map((error, index) => {
                return (
                  <div style={{ textAlign: 'left' }}>
                    {index + 1}.{' '}
                    {error.message || 'Při načítání dat nastala obecná chyba'}
                    {error.description && (
                      <div>
                        <small>{error.description}</small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AppError>
    );
  } else if (isLoading && !showContentWhenLoading) {
    return (
      <Spinner
        size={inline ? SpinnerSize.SMALL : SpinnerSize.STANDARD}
        className={spinnerCls}
      />
    );
  } else {
    return <>{(children as any)({ isFetching, hasData })}</>;
  }
};
